import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Breadcrumbs from "../components/ui/Breadcrumbs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Gift, CreditCard, Search, CheckCircle, Send } from "lucide-react";
import { API } from "../apiConfig";


const AMOUNTS = [25, 50, 100, 150, 200, 500];

const GiftCardsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [purchasedCard, setPurchasedCard] = useState(null);

  // Redeem state
  const [redeemCode, setRedeemCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [cardInfo, setCardInfo] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  // My cards
  const [myCards, setMyCards] = useState([]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (token) fetchMyCards();
  }, [token]);

  const fetchMyCards = async () => {
    try {
      const res = await axios.get(`${API}/gift-cards/my-cards`, { headers });
      setMyCards(res.data.cards || []);
    } catch {}
  };

  const handlePurchase = async () => {
    if (!token) {
      toast.error("Please sign in to purchase a gift card");
      navigate("/login");
      return;
    }
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/gift-cards/purchase`, {
        amount: selectedAmount,
        recipient_email: recipientEmail || null,
        recipient_name: recipientName || null,
        sender_name: user?.name || null,
        message: message || null,
      }, { headers });
      setPurchasedCard(res.data);
      toast.success("Gift card purchased!");
      fetchMyCards();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to purchase gift card");
    } finally {
      setPurchasing(false);
    }
  };

  const handleCheckBalance = async () => {
    if (!redeemCode.trim()) return;
    setChecking(true);
    setCardInfo(null);
    try {
      const res = await axios.get(`${API}/gift-cards/check/${redeemCode.trim()}`);
      setCardInfo(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gift card not found");
    } finally {
      setChecking(false);
    }
  };

  const handleRedeem = async () => {
    if (!token) {
      toast.error("Please sign in to redeem a gift card");
      navigate("/login");
      return;
    }
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/gift-cards/redeem`, {
        code: redeemCode.trim(),
      }, { headers });
      toast.success(res.data.message);
      setCardInfo(null);
      setRedeemCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to redeem");
    } finally {
      setRedeeming(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(price);

  return (
    <div className="min-h-screen bg-background" data-testid="gift-cards-page">
      <Navigation />

      <section className="pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Breadcrumbs items={[{ label: "Gift Cards" }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4">
              <Gift className="h-3 w-3 mr-1" /> E-Voucher
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4">
              Gift the Art Experience
            </h1>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Share the beauty of Aboriginal art with someone special. Digital
              gift cards are delivered instantly via email and valid for 12
              months.
            </p>
          </motion.div>

          <Tabs defaultValue="purchase" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="purchase" data-testid="tab-purchase">
                <Gift className="h-4 w-4 mr-2" /> Purchase
              </TabsTrigger>
              <TabsTrigger value="redeem" data-testid="tab-redeem">
                <CreditCard className="h-4 w-4 mr-2" /> Redeem
              </TabsTrigger>
            </TabsList>

            {/* ---- Purchase Tab ---- */}
            <TabsContent value="purchase">
              {purchasedCard ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-medium mb-2">Gift Card Ready!</h2>
                  <p className="text-muted-foreground mb-6">
                    {purchasedCard.recipient_email
                      ? `We've sent it to ${purchasedCard.recipient_email}`
                      : "Share the code below with the recipient"}
                  </p>
                  <Card className="max-w-sm mx-auto rounded-2xl">
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-muted-foreground mb-1">Gift Card Code</p>
                      <p className="text-2xl font-bold tracking-widest mb-2" data-testid="gift-card-code">
                        {purchasedCard.code}
                      </p>
                      <p className="text-primary text-xl font-medium">
                        {formatPrice(purchasedCard.amount)}
                      </p>
                    </CardContent>
                  </Card>
                  <Button
                    className="mt-6 rounded-full"
                    variant="outline"
                    onClick={() => setPurchasedCard(null)}
                  >
                    Purchase Another
                  </Button>
                </motion.div>
              ) : (
                <div className="grid md:grid-cols-5 gap-8">
                  <div className="md:col-span-3 space-y-6">
                    {/* Amount Selection */}
                    <div>
                      <Label className="mb-3 block">Select Amount</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {AMOUNTS.map((amt) => (
                          <button
                            key={amt}
                            onClick={() => setSelectedAmount(amt)}
                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                              selectedAmount === amt
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border hover:border-primary/50"
                            }`}
                            data-testid={`gift-amount-${amt}`}
                          >
                            <span className="text-xl font-bold">${amt}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Recipient Details */}
                    <div className="space-y-4">
                      <div>
                        <Label>Recipient's Name (optional)</Label>
                        <Input
                          value={recipientName}
                          onChange={(e) => setRecipientName(e.target.value)}
                          placeholder="Their name"
                          data-testid="gift-recipient-name"
                        />
                      </div>
                      <div>
                        <Label>Recipient's Email (optional)</Label>
                        <Input
                          type="email"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          placeholder="We'll email the gift card directly"
                          data-testid="gift-recipient-email"
                        />
                      </div>
                      <div>
                        <Label>Personal Message (optional)</Label>
                        <Textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Add a personal touch..."
                          rows={3}
                          data-testid="gift-message"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="md:col-span-2">
                    <Card className="rounded-2xl sticky top-28">
                      <CardContent className="pt-6">
                        <div className="text-center mb-6">
                          <div className="w-full aspect-[1.6/1] bg-gradient-to-br from-[#1a1612] to-[#2d2820] rounded-xl flex flex-col items-center justify-center mb-4 relative overflow-hidden">
                            <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 30% 50%, #d8964e 0%, transparent 50%)'}} />
                            <p className="text-[#d8964e] text-sm tracking-widest mb-1 relative z-10 font-brand">ART BY ARAWOL</p>
                            <p className="text-white text-4xl font-bold relative z-10">${selectedAmount}</p>
                            <p className="text-[#c4b99a] text-xs mt-1 relative z-10">DIGITAL GIFT CARD</p>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm mb-6">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Card Value</span>
                            <span className="font-medium">{formatPrice(selectedAmount)}</span>
                          </div>
                          {recipientEmail && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Deliver to</span>
                              <span className="truncate ml-2">{recipientEmail}</span>
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={handlePurchase}
                          disabled={purchasing}
                          className="w-full rounded-full"
                          data-testid="purchase-gift-card-btn"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {purchasing ? "Processing..." : `Purchase ${formatPrice(selectedAmount)} Gift Card`}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center mt-3">
                          Valid for 12 months. Redeemable on all artworks.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* My Purchased Cards */}
              {myCards.length > 0 && !purchasedCard && (
                <div className="mt-12">
                  <h3 className="text-lg font-medium mb-4">Your Purchased Gift Cards</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myCards.map((card) => (
                      <Card key={card.card_id} className="rounded-xl">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex justify-between items-start mb-2">
                            <code className="text-xs font-mono">{card.code}</code>
                            <Badge variant={card.status === "active" ? "default" : "secondary"}>
                              {card.status}
                            </Badge>
                          </div>
                          <p className="text-lg font-medium">{formatPrice(card.balance)} <span className="text-sm text-muted-foreground">/ {formatPrice(card.amount)}</span></p>
                          {card.recipient_email && (
                            <p className="text-xs text-muted-foreground mt-1">Sent to: {card.recipient_email}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ---- Redeem Tab ---- */}
            <TabsContent value="redeem">
              <div className="max-w-md mx-auto">
                <Card className="rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="text-center mb-6">
                      <CreditCard className="h-10 w-10 text-primary mx-auto mb-3" />
                      <h2 className="text-xl font-medium">Redeem a Gift Card</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Enter your gift card code to add the balance to your account
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label>Gift Card Code</Label>
                        <Input
                          value={redeemCode}
                          onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                          placeholder="ARAWOL-XXXX-XXXX"
                          className="text-center text-lg tracking-widest font-mono"
                          data-testid="redeem-code-input"
                        />
                      </div>

                      <Button
                        onClick={handleCheckBalance}
                        disabled={checking || !redeemCode.trim()}
                        variant="outline"
                        className="w-full rounded-full"
                        data-testid="check-balance-btn"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        {checking ? "Checking..." : "Check Balance"}
                      </Button>

                      {cardInfo && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-muted/50 rounded-xl text-center"
                        >
                          <p className="text-sm text-muted-foreground mb-1">Available Balance</p>
                          <p className="text-3xl font-bold text-primary" data-testid="card-balance">
                            {formatPrice(cardInfo.balance)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Original value: {formatPrice(cardInfo.original_amount)}
                          </p>
                          <Button
                            onClick={handleRedeem}
                            disabled={redeeming || cardInfo.balance <= 0}
                            className="mt-4 rounded-full w-full"
                            data-testid="redeem-btn"
                          >
                            {redeeming ? "Redeeming..." : `Add ${formatPrice(cardInfo.balance)} to My Account`}
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GiftCardsPage;
